import React, { memo } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Stack,
  Typography,
  type CardProps,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

export interface OrgLaneCardProps extends Omit<CardProps, "children"> {
  icon: React.ReactNode;
  title: string;
  color: "primary" | "secondary" | "success";
  /**
   * Conteúdo opcional exibido à direita do título, no header do card.
   * Útil para ações compactas (ex.: Switch, IconButton) sem acoplar o card a domínio.
   */
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}

const OrgLaneCard: React.FC<OrgLaneCardProps> = memo(
  ({ icon, title, color, headerRight, children, sx, ...cardProps }) => {
    return (
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          backgroundColor: (theme) => {
            const palette =
              color === "primary"
                ? theme.palette.primary
                : color === "secondary"
                  ? theme.palette.secondary
                  : theme.palette.success;

            return theme.palette.mode === "dark"
              ? alpha(palette.main, 0.14)
              : palette.light;
          },
          backgroundImage: (theme) => {
            const palette =
              color === "primary"
                ? theme.palette.primary
                : color === "secondary"
                  ? theme.palette.secondary
                  : theme.palette.success;

            return theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${alpha(palette.main, 0.35)} 0%, ${alpha(
                  palette.main,
                  0.18,
                )} 45%, rgba(255,255,255,0.03) 100%)`
              : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 45%, rgba(255,255,255,0.8) 100%)`;
          },
          boxShadow: (theme) => theme.shadows[1],
          ...sx,
        }}
        {...cardProps}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor:
                    color === "primary"
                      ? "primary.main"
                      : color === "secondary"
                        ? "secondary.main"
                        : "success.main",
                  color: "common.white",
                  flexShrink: 0,
                }}
              >
                {icon}
              </Avatar>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {title}
              </Typography>
            </Stack>

            {headerRight && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                {headerRight}
              </Stack>
            )}
          </Stack>
          {children}
        </CardContent>
      </Card>
    );
  },
);

OrgLaneCard.displayName = "OrgLaneCard";

export default OrgLaneCard;

